import { EmptyState } from '@/components/ui/empty-state';
import { Button, Grid, Text, VStack } from '@chakra-ui/react';
import type { Mention } from '@fluenceur/types';

import React, { useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MentionCardSkeleton from './MentionCardSkeleton';
import { MentionStatsProvider } from './mention-stats-context';
import { cssGridTemplateColumns, MENTIONS_GRID_GAP_PX } from './mentions-grid-layout';
import { MentionsVirtualGrid } from './MentionsVirtualGrid';
import { MessageText } from 'iconoir-react';

const INITIAL_SKELETON_COUNT = 6;

interface MentionsGridProps {
  mentions: Mention[];
  total?: number;
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  showCreator?: boolean;
  hasActiveFilters?: boolean;
  onResetFilters?: () => void;
  /** Denser layout for narrow contexts (e.g. the contact drawer): smaller tiles, tighter gaps, flat cards. */
  compact?: boolean;
  /** Media aspect ratio for each card. Defaults to portrait (4/5). */
  mediaAspectRatio?: string;
}

const MentionsGrid: React.FC<MentionsGridProps> = ({
  mentions,
  total,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onRetry,
  emptyStateTitle,
  emptyStateDescription,
  showCreator = true,
  hasActiveFilters = false,
  onResetFilters,
  compact = false,
  mediaAspectRatio,
}) => {
  const { t } = useTranslation();

  // Stats for every loaded mention are fetched by the provider in batches and
  // shared with the cards (no request per card).
  const mentionIds = useMemo(() => mentions.map((mention) => mention.id), [mentions]);

  const topMargin = compact ? 4 : 6;
  const gap = MENTIONS_GRID_GAP_PX[compact ? 'compact' : 'regular'];
  const cardAspectRatio = mediaAspectRatio ?? '4/5';

  // Track if we've ever loaded data to avoid skeleton on filter changes
  const hasLoadedDataRef = useRef(false);

  // Mark as loaded once we finish loading
  useEffect(() => {
    if (!isLoading) {
      hasLoadedDataRef.current = true;
    }
  }, [isLoading]);

  // Error State
  if (isError) {
    return (
      <EmptyState
        icon={<MessageText width={48} height={48} />}
        title={t('mentions.grid.errorTitle')}
        description={t('mentions.grid.errorDescription')}
      >
        {onRetry && (
          <Button onClick={onRetry} mt={4}>
            {t('mentions.grid.retry')}
          </Button>
        )}
      </EmptyState>
    );
  }

  // Loading State - only show skeleton on initial load, not on filter changes.
  // A handful of tiles, so a plain grid: nothing here needs virtualizing.
  if (isLoading && !hasLoadedDataRef.current) {
    return (
      <Grid
        templateColumns={cssGridTemplateColumns(compact)}
        columnGap={`${gap.column}px`}
        rowGap={`${gap.row}px`}
        w="full"
        mt={topMargin}
      >
        {Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, index) => (
          <MentionCardSkeleton
            key={`skeleton-${index}`}
            compact={compact}
            showCreator={showCreator}
            mediaAspectRatio={cardAspectRatio}
          />
        ))}
      </Grid>
    );
  }

  // Empty State
  if (!isLoading && !isError && mentions.length === 0) {
    const title = emptyStateTitle || t('mentions.grid.emptyTitle');
    const description = hasActiveFilters
      ? t('mentions.grid.emptyFiltered')
      : emptyStateDescription || t('mentions.grid.emptyDescription');

    return (
      <EmptyState
        icon={<MessageText width={48} height={48} />}
        title={title}
        description={description}
      >
        {hasActiveFilters && onResetFilters && (
          <Button onClick={onResetFilters} mt={4}>
            {t('mentions.grid.clearFilters')}
          </Button>
        )}
      </EmptyState>
    );
  }

  // Mentions Grid
  return (
    <VStack gap={4} w="full" align="flex-start" mt={topMargin}>
      <MentionStatsProvider mentionIds={mentionIds}>
        <MentionsVirtualGrid
          mentions={mentions}
          showCreator={showCreator}
          compact={compact}
          mediaAspectRatio={cardAspectRatio}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={onLoadMore}
        />
      </MentionStatsProvider>

      {!compact && mentions.length > 0 && (
        <Text fontSize="sm" color="gray.500" mx="auto">
          {typeof total === 'number' && total > mentions.length
            ? t('mentions.grid.showingOf', { count: mentions.length, total })
            : t('mentions.grid.showing', { count: mentions.length })}
        </Text>
      )}
    </VStack>
  );
};

export default MentionsGrid;
